import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './baseQueryWithReauth';

export interface PerkRatingSubmission {
  orderId: string;
  perkId: string;
  overallRating: number;
  perkRating: number;
  serviceRating: number;
  overallComment: string;
  perkComment: string;
  serviceComment: string;
}

export interface PerkRating {
  id: string;
  orderId: string;
  tenantId: string;
  customerId: string;
  perkId: string;
  overallRating: number;
  perkRating: number;
  serviceRating: number;
  overallComment: string;
  perkComment: string;
  serviceComment: string;
  verifiedPurchase: boolean;
  ratingWeight: number;
  timeDecayFactor: number;
  patternScore: number;
  appealed: boolean;
  appealStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface RatingAnalytics {
  tenantId: string;
  avgOverallRating: number;
  avgPerkRating: number;
  avgServiceRating: number;
  rating5StarCount: number;
  rating4StarCount: number;
  rating3StarCount: number;
  rating2StarCount: number;
  rating1StarCount: number;
  totalRatings: number;
  ratingsLast30Days: number;
  ratingsLast90Days: number;
  ratingTrend30Days: number;
  avgPatternScore: number;
  suspiciousRatingCount: number;
  buyBoxScoreModifier: number;
  minimumVolumeThresholdMet: boolean;
  lastRatingAt: string;
  lastAnalyticsUpdate: string;
}

export interface RatingAppeal {
  id: string;
  ratingId: string;
  tenantId: string;
  appealedBy: string;
  appealReason: string;
  appealEvidence: any;
  appealPriority: string;
  status: string;
  adminReviewer?: string;
  adminNotes?: string;
  resolutionReason?: string;
  resolvedAt?: string;
  resolutionAction?: string;
  createdAt: string;
  updatedAt: string;
}

export const ratingsApi = createApi({
  reducerPath: 'ratingsApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['PerkRating', 'RatingAnalytics', 'RatingAppeal'],
  endpoints: (builder) => ({
    submitPerkRating: builder.mutation({
      query: (ratingData: PerkRatingSubmission) => ({
        url: '/api/ratings/perk',
        method: 'POST',
        body: ratingData,
      }),
      invalidatesTags: ['RatingAnalytics'],
    }),

    getOrderRatings: builder.query({
      query: (orderId: string) => `/api/ratings/order/${orderId}`,
      providesTags: ['PerkRating'],
    }),

    getPartnerRatings: builder.query({
      query: () => '/api/ratings/partner',
      providesTags: ['PerkRating'],
    }),

    getRatingAnalytics: builder.query({
      query: () => '/api/ratings/analytics',
      providesTags: ['RatingAnalytics'],
    }),

    getAllRatings: builder.query({
      query: () => '/api/ratings/admin/all',
      providesTags: ['PerkRating'],
    }),

    submitRatingAppeal: builder.mutation({
      query: (appealData: any) => ({
        url: '/api/ratings/appeal',
        method: 'POST',
        body: appealData,
      }),
      invalidatesTags: ['RatingAppeal', 'PerkRating'],
    }),

    getPartnerAppeals: builder.query({
      query: () => '/api/ratings/appeals/partner',
      providesTags: ['RatingAppeal'],
    }),

    getAllAppeals: builder.query({
      query: () => '/api/ratings/appeals/admin',
      providesTags: ['RatingAppeal'],
    }),

    resolveAppeal: builder.mutation({
      query: ({ appealId, ...data }: any) => ({
        url: `/api/ratings/appeals/${appealId}/resolve`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['RatingAppeal', 'PerkRating', 'RatingAnalytics'],
    }),
  }),
});

export const {
  useSubmitPerkRatingMutation,
  useGetOrderRatingsQuery,
  useGetPartnerRatingsQuery,
  useGetRatingAnalyticsQuery,
  useGetAllRatingsQuery,
  useSubmitRatingAppealMutation,
  useGetPartnerAppealsQuery,
  useGetAllAppealsQuery,
  useResolveAppealMutation,
} = ratingsApi;